/**
 * UsersOverTimeChart - Area chart showing user activity trend over time.
 * Displays unique users with optional new users breakdown.
 */
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { chartColors, chartDefaults, formatChartDate, tooltipStyles } from './chartConfig'
import { Users } from 'lucide-react'
import type { UsersOverTimeDataPoint } from '@/types/adoption'

export interface UsersOverTimeChartProps {
  /** Array of data points */
  data: UsersOverTimeDataPoint[]
  /** Show new users as a separate area */
  showNewUsers?: boolean
  /** Chart height in pixels */
  height?: number
}

/**
 * Area chart displaying user activity over time with optional new users breakdown.
 * 
 * @example
 * ```tsx
 * <UsersOverTimeChart 
 *   data={[
 *     { date: '2026-02-13', uniqueUsers: 45, newUsers: 5, sessions: 120 },
 *     { date: '2026-02-14', uniqueUsers: 50, newUsers: 8, sessions: 135 },
 *   ]} 
 *   showNewUsers 
 * />
 * ```
 */
export function UsersOverTimeChart({
  data,
  showNewUsers = false,
  height = chartDefaults.height,
}: UsersOverTimeChartProps) {
  // Empty state
  if (!data || data.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        <Users className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No data available</p>
      </div>
    )
  }

  // Format data for display
  const formattedData = data.map(item => ({
    ...item,
    displayDate: formatChartDate(item.date),
  }))

  return (
    <div 
      role="img" 
      aria-label="Users over time area chart showing user activity trends"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <AreaChart 
          data={formattedData} 
          margin={chartDefaults.margin}
        >
          <defs>
            <linearGradient id="uniqueUsersGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColors.info} stopOpacity={0.3} />
              <stop offset="95%" stopColor={chartColors.info} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="newUsersGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColors.success} stopOpacity={0.3} />
              <stop offset="95%" stopColor={chartColors.success} stopOpacity={0} />
            </linearGradient>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={chartColors.grid}
            opacity={0.5}
          />
          
          <XAxis 
            dataKey="displayDate" 
            tick={{ fontSize: 12, fill: chartColors.muted }}
            tickLine={false}
            axisLine={{ stroke: chartColors.grid }}
          />
          
          <YAxis 
            tick={{ fontSize: 12, fill: chartColors.muted }}
            tickLine={false}
            axisLine={{ stroke: chartColors.grid }}
            width={40}
          />
          
          <Tooltip 
            contentStyle={tooltipStyles.contentStyle}
            labelStyle={tooltipStyles.labelStyle}
          />
          
          {showNewUsers && <Legend />}
          
          {/* Unique Users Area */}
          <Area
            type="monotone"
            dataKey="uniqueUsers"
            name="Unique Users"
            stroke={chartColors.info}
            fill="url(#uniqueUsersGradient)"
            strokeWidth={2}
          />
          
          {/* New Users Area (optional) */}
          {showNewUsers && (
            <Area
              type="monotone"
              dataKey="newUsers"
              name="New Users"
              stroke={chartColors.success}
              fill="url(#newUsersGradient)"
              strokeWidth={2}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
